import { useCallback, useEffect, useRef } from 'react'

export type HorizontalWheelScrollOptions = {
  /**
   * 为 true（默认）时：纵向滚轮优先转为横向拖动该元素（多用于窄屏下的横向芯片条）。
   * 为 false 时：完全不拦截纵向滚轮，纵向滚动交由浏览器链条（更适合时间线图等整块随页面上下滚）。
   */
  mapVerticalWheelToHorizontal?: boolean
}

function computedOverflowYAllowsScroll(el: HTMLElement): boolean {
  const y = getComputedStyle(el).overflowY
  return y === 'auto' || y === 'scroll' || y === 'overlay'
}

/**
 * 纵向滚轮转为横向滚动；到头时不拦截，交给浏览器或最近真正可纵向滚动的祖先。
 */
export function useHorizontalWheelScroll<T extends HTMLElement>(
  options: HorizontalWheelScrollOptions = {},
) {
  const mapVerticalWheelToHorizontal = options.mapVerticalWheelToHorizontal !== false
  const cleanupRef = useRef<(() => void) | null>(null)

  const setupScroll = useCallback(
    (el: T | null) => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
      if (!el) return

      const updateOverflowFlag = () => {
        const overflowing = el.scrollWidth - el.clientWidth > 1
        el.toggleAttribute('data-x-overflowing', overflowing)
        if (!overflowing && el.scrollLeft !== 0) {
          el.scrollLeft = 0
        }
      }

      updateOverflowFlag()
      const ro = new ResizeObserver(updateOverflowFlag)
      ro.observe(el)
      for (const child of Array.from(el.children)) {
        ro.observe(child)
      }
      const mo = new MutationObserver(() => {
        updateOverflowFlag()
        for (const child of Array.from(el.children)) {
          ro.observe(child)
        }
      })
      mo.observe(el, { childList: true, subtree: true })
      window.addEventListener('resize', updateOverflowFlag)

      if (!mapVerticalWheelToHorizontal) {
        cleanupRef.current = () => {
          ro.disconnect()
          mo.disconnect()
          window.removeEventListener('resize', updateOverflowFlag)
        }
        return
      }

      const normalizeDy = (e: WheelEvent) => {
        let dy = e.deltaY
        if (e.deltaMode === 1) dy *= 16
        else if (e.deltaMode === 2) dy *= el.clientHeight ?? 480
        return dy
      }

      const onWheel = (e: WheelEvent) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return

        const dy = normalizeDy(e)
        const edgeEps = 2

        const max = Math.max(0, el.scrollWidth - el.clientWidth)
        const { scrollLeft } = el
        const atStart = scrollLeft <= edgeEps
        const atEnd = max <= edgeEps || scrollLeft >= max - edgeEps

        if (max > edgeEps) {
          if (dy > 0 && !atEnd) {
            e.preventDefault()
            el.scrollLeft = Math.min(max, scrollLeft + dy)
            return
          }
          if (dy < 0 && !atStart) {
            e.preventDefault()
            el.scrollLeft = Math.max(0, scrollLeft + dy)
            return
          }
        }

        let p: HTMLElement | null = el.parentElement
        while (p) {
          if (!computedOverflowYAllowsScroll(p)) {
            p = p.parentElement
            continue
          }
          const oy = p.scrollHeight - p.clientHeight
          if (oy > edgeEps) {
            const st = p.scrollTop
            if ((dy > 0 && st < oy - edgeEps) || (dy < 0 && st > edgeEps)) {
              e.preventDefault()
              p.scrollTop = Math.max(0, Math.min(oy, st + dy))
              return
            }
          }
          p = p.parentElement
        }
        /* 未处理则勿 preventDefault，交由浏览器默认链条（时间线外层 scroll-region） */
      }

      el.addEventListener('wheel', onWheel, { passive: false, capture: true })
      cleanupRef.current = () => {
        el.removeEventListener('wheel', onWheel, true)
        ro.disconnect()
        mo.disconnect()
        window.removeEventListener('resize', updateOverflowFlag)
      }
    },
    [mapVerticalWheelToHorizontal],
  )

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
  }, [])

  return setupScroll
}
